import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import {
  catchError,
  debounceTime,
  delay,
  map,
  Observable,
  pipe,
  share,
} from 'rxjs'
import { environment } from 'src/environments/environment'
import { Schema, z } from 'zod'

import { ContactListSchema, ContactSchema, UserTokenSchema } from '../schemas'
import { IErrorRes } from '../schemas/base.schema'
import { endpoints } from './endpoints'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient)
  private _isAuthenticated = signal(false)
  private tokenKey = 'gef-token'
  private router = inject(Router)

  public routes = endpoints

  constructor() {
    if (this.getToken() !== null) {
      this._isAuthenticated.set(true)
    }
  }

  private setToken(token: string) {
    localStorage.setItem(this.tokenKey, token)
  }
  private getToken() {
    return localStorage.getItem(this.tokenKey)
  }
  private clearToken() {
    localStorage.removeItem(this.tokenKey)
  }

  /**
   * Read-only getter for seeing if the current user is authenticated
   */
  public isAuthenticated = this._isAuthenticated.asReadonly()

  /**
   * Ref: https://dev.to/danielsogl/from-flaky-to-flawless-angular-api-response-management-with-zod-4013
   */
  private verifyResponse<T extends z.ZodTypeAny>(zodObj?: T) {
    if (!zodObj) {
      return pipe()
    }
    return pipe(
      map((response) => {
        try {
          console.log('pre zod:', response)
          const res = zodObj.parse(response)
          console.log('zod res:', res)
          return res
        } catch (e) {
          console.warn(e)

          return response
        }
        // if (isDevMode()) {
        //   // TODO: Only in dev mode?
        //   const result = zodObj.safeParse(response)

        //   if (!result.success) {
        //     console.error('Obj zod error:', response, result.error)
        //   }
        // }
        // return response as z.infer<T>
      }),
    )
  }

  public request<T = unknown, B = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    schema?: Schema<T>,
    config?: {
      isPublic?: boolean
      body?: B
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[]
          }
      context?: HttpContext
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>
          }
      reportProgress?: boolean
      withCredentials?: boolean
      transferCache?:
        | {
            includeHeaders?: string[]
          }
        | boolean
    },
  ): Observable<T> {
    config = config ?? {}
    const isPublic = config?.isPublic

    if (!isPublic) {
      config['headers'] = {
        authorization: `Token ${this.getToken()}`,
        ...(config.headers ?? {}),
      }
    }

    return this.http
      .request<T>(method, url, config)
      .pipe(debounceTime(5000))
      .pipe(delay(environment.NETWORK_DELAY))
      .pipe(
        catchError((err) => {
          console.log('error from req:', err)
          throw err.error.errors
        }),
      )
      .pipe(
        catchError((errs: IErrorRes[]) => {
          if (!Array.isArray(errs)) {
            errs = [errs]
          }
          if (errs.some((e) => e?.code === 'authentication_failed')) {
            this.logout()
          }
          throw errs
        }),
      )
      .pipe(this.verifyResponse(schema))
      .pipe(share())
  }

  listContacts(url?: string) {
    return this.request(
      'GET',
      this.routes.contacts.list ?? url,
      ContactListSchema,
    )
  }

  getContact(id: number) {
    return this.request('GET', this.routes.contacts.detail(id), ContactSchema)
  }

  login(email: string, password: string) {
    // const body = UserLoginSchema.parse({ email, password })

    const res = this.request('POST', this.routes.users.token, UserTokenSchema, {
      isPublic: true,
      body: { email, password },
    })

    res.subscribe({
      next: ({ token }) => {
        this.setToken(token)
        this._isAuthenticated.set(true)
      },
      // error: (res) => {
      //   const body = ErrorSchema.parse(res)
      //   return body
      // },
    })

    return res
  }

  logout() {
    this.clearToken()
    this._isAuthenticated.set(false)
    this.router.navigate(['login'])
  }
}
